"use client"
import {AppIconSkeleton} from "@/app/components/atomic/AppIcon"
import FilterList from "@/app/components/auctor/filter/FilterList"
import ResultList from "@/app/components/auctor/result/ResultList"
import {IAppliedFilter, IFiltersConfigPayload} from "@/app/models/auctor"
import {getClientQueryParams} from "@/app/utils/expression-utils"
import {
    applyFiltersOnResults,
    evaluateDynamicDefaultValue,
    initAppliedFilters,
    sortBetweenFeaturedAndNormalResults,
} from "@/app/utils/filters-utils"
import dynamic from "next/dynamic"
import {useEffect, useState} from "react"

const AppIcon = dynamic(() => import("@/app/components/atomic/AppIcon"), {
    loading: () => <AppIconSkeleton/>,
})

const LIST_LOADING_DURATION = 100

interface ComparatorProps {
    comparatorId: number
    templateConfig: any
    initialResults: any
    filtersConfig: IFiltersConfigPayload
    pageConfig?: any
    isPreview?: boolean
    showFiltersAbove?: boolean
    maxFiltersMobile?: number
}

export default function Comparator({
                                       comparatorId,
                                       templateConfig,
                                       initialResults,
                                       filtersConfig,
                                       pageConfig,
                                       isPreview,
                                       maxFiltersMobile = 2,
                                   }: ComparatorProps) {
    const queryParams = getClientQueryParams()

    const initialAppliedFilters = initAppliedFilters(filtersConfig.filters, pageConfig, queryParams)

    const [appliedFilters, setAppliedFilters] = useState<IAppliedFilter[]>(initialAppliedFilters)
    const [filteredResults, setFilteredResults] = useState<any[]>(initialResults ?? [])
    const [featuredResults, setFeaturedResults] = useState<any[]>([])
    const [fullResultsPayload, setFullResultsPayload] = useState<any[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(true)

    const [isFilterListExpandedOnMobile, setIsFilterListExpandedOnMobile] = useState<boolean>(false)
    // FilterList masque le surplus en CSS sur mobile : le plafond est le même pour tous les viewports.
    const maxFilterToDisplay = maxFiltersMobile

    const updateAppliedFilter = (filterToUpdate: IAppliedFilter, newValue: any) => {
        setAppliedFilters((prevAppliedFilters) => {
            let updatedFilters = prevAppliedFilters.map((aFilter) => {
                if (aFilter.filterId === filterToUpdate.filterId) {
                    return {...aFilter, value: newValue}
                }
                return aFilter
            })

            updatedFilters = updatedFilters.map((aFilter) => {
                if (aFilter.dynamicValueExpression && aFilter.filterId !== filterToUpdate.filterId) {
                    const filterConfig = filtersConfig.filters.find(
                        (f: any) => f.unique_id === aFilter.filterId
                    )
                    if (filterConfig) {
                        const dynamicValue = evaluateDynamicDefaultValue(filterConfig, updatedFilters, queryParams)
                        if (dynamicValue !== null && dynamicValue !== undefined) {
                            return {...aFilter, value: dynamicValue}
                        }
                    }
                }
                return aFilter
            })

            return updatedFilters
        })

        setIsLoading(true)
        setTimeout(() => setIsLoading(false), LIST_LOADING_DURATION)
    }

    const filterResults = (results: any[], appliedFilters: IAppliedFilter[]) => {
        const fResults = applyFiltersOnResults(
            results,
            filtersConfig.filters,
            appliedFilters,
            filtersConfig.expression_filter,
            queryParams
        )

        const sortedResults = sortBetweenFeaturedAndNormalResults(
            fResults,
            appliedFilters,
            filtersConfig.featured_offer_configs,
            queryParams
        )

        setFilteredResults(sortedResults.results)
        setFeaturedResults(sortedResults.featuredResults)
    }

    const hasFilters = filtersConfig?.filters?.length > 0

    useEffect(() => {
        if (fullResultsPayload.length === 0) {
            return
        }
        filterResults(fullResultsPayload, appliedFilters)
    }, [appliedFilters, fullResultsPayload, filtersConfig])

    useEffect(() => {
        setIsLoading(false)
        fetch(`/api/auctor/comparator-data?comparatorId=${comparatorId}`)
            .then((response) => response.json())
            .then((res) => {
                if (!res || !Array.isArray(res)) {
                    console.error('Invalid response from getComparatorResultsData:', res)
                    setFullResultsPayload([])
                    return
                }
                setFullResultsPayload(res)

                if (isPreview) {
                    filterResults(res, appliedFilters)
                }
            })
            .catch((error) => {
                console.error('Error fetching comparator results:', error)
                setFullResultsPayload([])
            })
    }, [])

    return (
        <div
            id="comparator-section"
            className="relative w-full bg-neutral flex items-center justify-center"
            data-test="comparator-section"
        >
            <div className="w-full px-2 md:px-6 py-4 h-full max-w-7xl my-2 lg:mt-4 z-10">
                <div
                    className="flex justify-center relative flex-col gap-4 xl:flex-row place-self-center mx-auto w-full"
                >
                    {hasFilters && (
                        <div
                            className="grid items-center content-center h-fit min-w-[300px] z-10 bg-white rounded-2xl shadow-xl p-4"
                            data-test="comparator-filters"
                        >
                            <FilterList
                                className="gap-6 flex flex-col"
                                filtersConfig={filtersConfig?.filters}
                                appliedFilters={appliedFilters}
                                updateAppliedFilter={updateAppliedFilter}
                                displayNumberOnMobile={maxFilterToDisplay}
                                isFilterListExpandedOnMobile={isFilterListExpandedOnMobile}
                                queryParams={queryParams}
                            />

                            {filtersConfig?.filters?.length > maxFilterToDisplay && (
                                <div
                                    className="flex md:hidden bg-rm-blue text-white p-2 rounded-b-md cursor-pointer justify-center items-center"
                                    onClick={() => setIsFilterListExpandedOnMobile(!isFilterListExpandedOnMobile)}
                                >
                                    <AppIcon
                                        type="regular"
                                        iconClassName={isFilterListExpandedOnMobile ? "fa-circle-minus" : "fa-circle-plus"}
                                        className="mr-2"
                                    />
                                    Afficher {isFilterListExpandedOnMobile ? "moins" : "plus"} de critères
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex flex-col h-full w-full">
                        {!isLoading && filteredResults.length === 0 && (
                            <div className={"flex flex-col items-center my-auto gap-4"}>
                                <h2 className={"text-2xl text-center"}>Oups !</h2>
                                <p className={"text-center"}>
                                    Nous n&apos;avons pas d&apos;offre à vous proposer pour ce profil.
                                </p>
                            </div>
                        )}

                        <ResultList
                            isPreview={isPreview}
                            templateConfig={templateConfig}
                            appliedFilters={appliedFilters}
                            featuredResults={featuredResults}
                            results={filteredResults}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}
